import { ref, computed } from 'vue'
import { logger, createLogger } from '@/utils/logger'

const fileLogger = createLogger('useLocaleDetection')

/**
 * Supported locales in the application
 */
export type SupportedLocale = 'en-GB' | 'en-US' | 'es-ES' | 'fr-FR' | 'de-DE' | 'it-IT' | 'pt-PT' | 'nl-NL' | 'sk-SK' | 'ru-RU' | 'ar-SA'

/**
 * Country to locale mapping
 * Maps ISO 3166-1 alpha-2 country codes to supported locales
 */
export const countryToLocaleMap: Record<string, SupportedLocale> = {
  // English speaking countries
  'GB': 'en-GB',
  'US': 'en-US',
  'CA': 'en-US', // Canada - default to US English
  'AU': 'en-US', // Australia - default to US English
  'NZ': 'en-US', // New Zealand - default to US English
  'IE': 'en-GB', // Ireland - default to GB English
  'ZA': 'en-GB', // South Africa - default to GB English
  
  // Spanish speaking countries
  'ES': 'es-ES',
  'MX': 'es-ES',
  'AR': 'es-ES',
  'CO': 'es-ES',
  'CL': 'es-ES',
  'PE': 'es-ES',
  'VE': 'es-ES',
  'EC': 'es-ES',
  'GT': 'es-ES',
  'CU': 'es-ES',
  'BO': 'es-ES',
  'DO': 'es-ES',
  'HN': 'es-ES',
  'PY': 'es-ES',
  'SV': 'es-ES',
  'NI': 'es-ES',
  'CR': 'es-ES',
  'PA': 'es-ES',
  'UY': 'es-ES',
  
  // French speaking countries
  'FR': 'fr-FR',
  'BE': 'fr-FR', // Belgium
  'CH': 'fr-FR', // Switzerland - default to French
  'LU': 'fr-FR', // Luxembourg
  'MC': 'fr-FR', // Monaco
  // 'CA': 'fr-FR', // Canada - French regions
  'SN': 'fr-FR', // Senegal
  'CI': 'fr-FR', // Côte d'Ivoire
  'CM': 'fr-FR', // Cameroon
  // 'MA': 'fr-FR', // Morocco
  
  // German speaking countries
  'DE': 'de-DE',
  'AT': 'de-DE', // Austria
  'LI': 'de-DE', // Liechtenstein
  // 'CH': 'de-DE', // Switzerland - can be German
  
  // Italian speaking countries
  'IT': 'it-IT',
  'SM': 'it-IT', // San Marino
  'VA': 'it-IT', // Vatican City
  // 'CH': 'it-IT', // Switzerland - can be Italian
  
  // Portuguese speaking countries
  'PT': 'pt-PT',
  'BR': 'pt-PT', // Brazil
  'AO': 'pt-PT', // Angola
  'MZ': 'pt-PT', // Mozambique
  
  // Dutch speaking countries
  'NL': 'nl-NL',
  // 'BE': 'nl-NL', // Belgium - Flemish regions
  
  // Slovak speaking countries
  'SK': 'sk-SK',
  
  // Russian speaking countries
  'RU': 'ru-RU',
  'BY': 'ru-RU', // Belarus
  'KZ': 'ru-RU', // Kazakhstan
  'KG': 'ru-RU', // Kyrgyzstan
  'UA': 'ru-RU', // Ukraine
  
  // Arabic speaking countries
  'SA': 'ar-SA', // Saudi Arabia
  'AE': 'ar-SA', // UAE
  'EG': 'ar-SA', // Egypt
  'JO': 'ar-SA', // Jordan
  'LB': 'ar-SA', // Lebanon
  'MA': 'ar-SA', // Morocco
  'IQ': 'ar-SA', // Iraq
  'DZ': 'ar-SA', // Algeria
  'SD': 'ar-SA', // Sudan
  'YE': 'ar-SA', // Yemen
  'SY': 'ar-SA', // Syria
  'TN': 'ar-SA', // Tunisia
  'SO': 'ar-SA', // Somalia
  'LY': 'ar-SA', // Libya
  'OM': 'ar-SA', // Oman
  'KW': 'ar-SA', // Kuwait
  'QA': 'ar-SA', // Qatar
  'BH': 'ar-SA', // Bahrain
}

/**
 * Browser language to locale mapping
 * Maps browser language codes to supported locales
 */
export const browserLanguageToLocaleMap: Record<string, SupportedLocale> = {
  'en': 'en-GB',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  'en-CA': 'en-US',
  'en-AU': 'en-US',
  'en-NZ': 'en-US',
  'es': 'es-ES',
  'es-ES': 'es-ES',
  'es-MX': 'es-ES',
  'es-AR': 'es-ES',
  'fr': 'fr-FR',
  'fr-FR': 'fr-FR',
  'fr-BE': 'fr-FR',
  'fr-CA': 'fr-FR',
  'de': 'de-DE',
  'de-DE': 'de-DE',
  'de-AT': 'de-DE',
  'it': 'it-IT',
  'it-IT': 'it-IT',
  'pt': 'pt-PT',
  'pt-PT': 'pt-PT',
  'pt-BR': 'pt-PT',
  'nl': 'nl-NL',
  'nl-NL': 'nl-NL',
  'nl-BE': 'nl-NL',
  'sk': 'sk-SK',
  'sk-SK': 'sk-SK',
  'ru': 'ru-RU',
  'ru-RU': 'ru-RU',
  'ru-BY': 'ru-RU',
  'ru-KZ': 'ru-RU',
  'ar': 'ar-SA',
  'ar-SA': 'ar-SA',
  'ar-AE': 'ar-SA',
  'ar-EG': 'ar-SA',
  'ar-JO': 'ar-SA',
  'ar-LB': 'ar-SA',
}

/**
 * Default locale fallback
 */
export const DEFAULT_LOCALE: SupportedLocale = 'en-GB'

/**
 * Storage key for user locale preference
 */
const LOCALE_STORAGE_KEY = 'user_locale_preference'

export interface LocaleDetectionOptions {
  /**
   * Whether to use IP-based geolocation for detection
   * @default false
   */
  useIpGeolocation?: boolean
  
  /**
   * Whether to prioritize stored user preference
   * @default true
   */
  prioritizeStoredPreference?: boolean
  
  /**
   * Custom geolocation API endpoint (optional)
   * If provided, should return { country: 'XX' } format
   */
  geolocationApiUrl?: string
}

/**
 * Composable for detecting and managing user locale based on location
 */
export function useLocaleDetection(options: LocaleDetectionOptions = {}) {
  const {
    useIpGeolocation = false,
    prioritizeStoredPreference = true,
    geolocationApiUrl
  } = options

  const detectedLocale = ref<SupportedLocale | null>(null)
  const isDetecting = ref(false)
  const detectionError = ref<Error | null>(null)

  /**
   * Get user's stored locale preference
   */
  const getStoredLocalePreference = (): SupportedLocale | null => {
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
      if (stored) {
        const locale = stored as SupportedLocale
        // Validate that stored locale is supported
        if (Object.values(countryToLocaleMap).includes(locale) || 
            locale === 'en-GB' || locale === 'en-US') {
          return locale
        }
      }
    } catch (error) {
      fileLogger.warn('Failed to read stored locale preference:', error)
    }
    return null
  }

  /**
   * Store user's locale preference
   */
  const storeLocalePreference = (locale: SupportedLocale): void => {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale)
      fileLogger.log(`Stored locale preference: ${locale}`)
    } catch (error) {
      fileLogger.warn('Failed to store locale preference:', error)
    }
  }

  /**
   * Get browser language
   */
  const getBrowserLanguage = (): SupportedLocale => {
    if (typeof navigator === 'undefined') {
      return DEFAULT_LOCALE
    }

    // Get browser language(s)
    const languages = navigator.languages || [navigator.language]
    
    // Try to match exact language code first
    for (const lang of languages) {
      const normalizedLang = lang.toLowerCase()
      if (browserLanguageToLocaleMap[normalizedLang]) {
        return browserLanguageToLocaleMap[normalizedLang]
      }
      
      // Try to match language code without region
      const langCode = normalizedLang.split('-')[0]
      if (browserLanguageToLocaleMap[langCode]) {
        return browserLanguageToLocaleMap[langCode]
      }
    }

    return DEFAULT_LOCALE
  }

  /**
   * Detect country from IP using geolocation API
   */
  const detectCountryFromIp = async (): Promise<string | null> => {
    try {
      // Use provided API or default free service
      const apiUrl = geolocationApiUrl || 'https://ipapi.co/country/'
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': geolocationApiUrl ? 'application/json' : 'text/plain',
        },
        // Add timeout to prevent hanging
        signal: AbortSignal.timeout(5000)
      })

      if (!response.ok) {
        throw new Error(`Geolocation API returned ${response.status}`)
      }

      // Handle different response types
      let country: string | null = null
      
      const contentType = response.headers.get('content-type') || ''
      
      if (contentType.includes('application/json')) {
        // JSON response (custom API or ipapi.co/json/)
        const data = await response.json()
        country = data.country || data.country_code || data.countryCode || data.country_code_iso2
      } else {
        // Plain text response (ipapi.co/country/)
        const text = await response.text()
        const trimmed = text.trim()
        if (trimmed.length === 2) {
          country = trimmed
        }
      }
      
      if (country && typeof country === 'string' && country.length === 2) {
        return country.toUpperCase()
      }

      return null
    } catch (error) {
      fileLogger.warn('IP geolocation detection failed:', error)
      return null
    }
  }

  /**
   * Map country code to locale
   */
  const mapCountryToLocale = (countryCode: string): SupportedLocale => {
    return countryToLocaleMap[countryCode] || DEFAULT_LOCALE
  }

  /**
   * Detect locale based on location and preferences
   */
  const detectLocale = async (): Promise<SupportedLocale> => {
    isDetecting.value = true
    detectionError.value = null

    try {
      // 1. Check stored user preference first (if prioritized)
      if (prioritizeStoredPreference) {
        const stored = getStoredLocalePreference()
        if (stored) {
          fileLogger.log(`Using stored locale preference: ${stored}`)
          detectedLocale.value = stored
          return stored
        }
      }

      // 2. Try IP-based geolocation if enabled
      if (useIpGeolocation) {
        const country = await detectCountryFromIp()
        if (country) {
          const locale = mapCountryToLocale(country)
          fileLogger.log(`Detected locale from IP geolocation (${country}): ${locale}`)
          detectedLocale.value = locale
          return locale
        }
      }

      // 3. Fall back to browser language
      const browserLocale = getBrowserLanguage()
      fileLogger.log(`Using browser language: ${browserLocale}`)
      detectedLocale.value = browserLocale
      return browserLocale

    } catch (error) {
      detectionError.value = error instanceof Error ? error : new Error(String(error))
      fileLogger.error('Locale detection failed:', error)
      detectedLocale.value = DEFAULT_LOCALE
      return DEFAULT_LOCALE
    } finally {
      isDetecting.value = false
    }
  }

  /**
   * Set locale preference (user selected)
   */
  const setLocale = (locale: SupportedLocale): void => {
    detectedLocale.value = locale
    storeLocalePreference(locale)
    fileLogger.log(`Locale set to: ${locale}`)
  }

  /**
   * Clear stored locale preference (fall back to detection)
   */
  const clearLocalePreference = (): void => {
    try {
      localStorage.removeItem(LOCALE_STORAGE_KEY)
      fileLogger.log('Cleared stored locale preference')
    } catch (error) {
      fileLogger.warn('Failed to clear locale preference:', error)
    }
  }

  return {
    detectedLocale: computed(() => detectedLocale.value),
    isDetecting: computed(() => isDetecting.value),
    detectionError: computed(() => detectionError.value),
    detectLocale,
    setLocale,
    clearLocalePreference,
    getBrowserLanguage,
    getStoredLocalePreference,
    DEFAULT_LOCALE
  }
}

